import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { humanize } from '../theme/adminVisualSystem';
import { TONE } from '../theme/adminVisualSystem';
import SectionAnalyticsHeader from './SectionAnalyticsHeader';
import ArtistWorkCard from './ArtistWorkCard';
import ArtistWorkPreviewModal from './ArtistWorkPreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USAGE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'gallery_exhibition', label: 'Gallery Exhibition' },
  { value: 'website', label: 'Website' },
  { value: 'unused', label: 'Unused' },
];

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'other', label: 'Other' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'archived', label: 'Archived' },
];

function FilterSelect({ items, value, onChange, label, testIdPrefix }) {
  return (
    <label className="inline-flex items-center gap-2 text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">
      <span className="text-[#555]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testIdPrefix}-select`}
        className="bg-[#0a0a0a] border border-[#222] text-[#d6d6d6] font-mono text-[11px] uppercase tracking-[0.1em] px-2 py-1.5 rounded-none focus:outline-none focus:border-[#90E0EF] cursor-pointer"
      >
        {items.map((it) => (
          <option key={it.value} value={it.value} data-testid={`${testIdPrefix}-${it.value}`}>
            {it.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * ArtistWorksGrid — works overview.
 *
 * Props:
 *   artistId
 *   compact   — if true, omits the SectionAnalyticsHeader and shrinks filter row
 *               (used inside Profile tab so the page reads like a portfolio).
 */
export default function ArtistWorksGrid({ artistId, compact = false }) {
  const [works, setWorks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState('all');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (usage !== 'all') params.set('usage', usage);
      if (type !== 'all') params.set('file_type', type);
      if (status !== 'all') params.set('status', status);
      const res = await axios.get(`${API}/admin/artists/${artistId}/works?${params.toString()}`);
      setWorks(res.data.works || []);
      setStats(res.data.stats || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [artistId, usage, type, status]);

  const updateLocal = (updated) => {
    setWorks((arr) => arr.map((w) => (w.id === updated.id ? updated : w)));
    setSelected(updated);
    // Refresh stats from server in the background
    axios.get(`${API}/admin/artists/${artistId}/works`).then((res) => setStats(res.data.stats || {})).catch(() => {});
  };

  const headerStats = useMemo(() => ([
    { label: 'Images', value: stats.images || 0, tone: TONE.cyan },
    { label: 'Videos', value: stats.videos || 0, tone: TONE.violet },
    { label: 'Other', value: stats.other || 0, tone: TONE.muted },
    { label: 'Social', value: stats.social_media || 0, tone: TONE.cyan },
    { label: 'Gallery', value: stats.gallery_exhibition || 0, tone: TONE.green },
    { label: 'Website', value: stats.website || 0, tone: TONE.violet },
    { label: 'Unused', value: stats.unused || 0, tone: TONE.muted },
  ]), [stats]);

  return (
    <div className="space-y-5">
      {!compact && (
        <SectionAnalyticsHeader
          title="Artist Works"
          helper="Files submitted by this artist across exhibitions"
          items={works}
          chartLabel="Submission Inflow"
          days={30}
          stats={headerStats}
          totalsFromItems={false}
        />
      )}

      {compact && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10px] font-mono text-[#888]">
          <span className="text-[#666] uppercase tracking-[0.18em]">Total</span>
          <span className="text-white">{stats.total || 0}</span>
          <span className="text-[#333]">·</span>
          <span><span className="text-[#90E0EF]">{stats.images || 0}</span> images</span>
          <span><span className="text-[#c89dff]">{stats.videos || 0}</span> videos</span>
          {stats.other > 0 && <span><span className="text-[#aaa]">{stats.other}</span> other</span>}
          <span className="text-[#333]">·</span>
          <span><span className="text-[#7ed4a0]">{stats.gallery_exhibition || 0}</span> gallery</span>
          <span><span className="text-[#90E0EF]">{stats.social_media || 0}</span> social</span>
          <span><span className="text-[#c89dff]">{stats.website || 0}</span> web</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
        <FilterSelect items={USAGE_FILTERS} value={usage} onChange={setUsage} label="Usage" testIdPrefix="works-usage" />
        <FilterSelect items={TYPE_FILTERS} value={type} onChange={setType} label="Type" testIdPrefix="works-type" />
        <FilterSelect items={STATUS_FILTERS} value={status} onChange={setStatus} label="Status" testIdPrefix="works-status" />
      </div>

      {loading ? (
        <p className="text-[#444] font-mono text-sm">Loading works…</p>
      ) : works.length === 0 ? (
        <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
          <p className="text-[#666] font-mono text-sm">No works yet</p>
          <p className="text-[11px] font-mono text-[#444] mt-2">Send the artist intake form to collect their materials.</p>
        </div>
      ) : (
        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          data-testid="artist-works-grid"
        >
          {works.map((w) => (
            <ArtistWorkCard key={w.id} work={w} onClick={() => setSelected(w)} />
          ))}
        </motion.div>
      )}

      <ArtistWorkPreviewModal
        work={selected}
        onClose={() => setSelected(null)}
        onUpdate={updateLocal}
      />
    </div>
  );
}
